import type { Assay } from '../api'

/**
 * The record, in the words somebody would use out loud.
 *
 * ## Why this exists next to the Assay plate
 *
 * The plate is right. It is a 6×6 Admiralty grid with a staircase floor, a custody-delay
 * profile and a chronology drawn against signed steps from that floor — and every one of
 * those is the correct instrument for the question. It is also unreadable to the person who
 * has to decide whether a claim can go in a speech, and in politics that person is not an
 * analyst. A board only an analyst can read gets ignored, and then the decision gets made
 * without it, which is the failure the board existed to prevent.
 *
 * So this says the same thing in four sentences. It is a TRANSLATION, not a simplification:
 * nothing here rounds away a caveat. Undated stays its own category rather than folding into
 * "fast", single-source stays "nobody has repeated it" rather than becoming "doubtful", and
 * the floor is named in plain terms rather than hidden.
 */
export function PlainStanding({ assay }: { assay: Assay }) {
  const s = assay.standing
  const pct = (n: number) => (s.items > 0 ? Math.round((n / s.items) * 100) : 0)
  const hours = (m: number) => (m < 60 ? `${Math.round(m)} minutes`
    : m < 60 * 48 ? `${(m / 60).toFixed(0)} hours` : `${Math.round(m / 1440)} days`)

  const solid = pct(s.atOrAboveFloor)
  const verdict = s.items === 0
    ? 'There is nothing in this window to judge.'
    : solid >= 70
      ? 'Most of this file would survive being challenged.'
      : solid >= 40
        ? 'Some of this file would survive being challenged, and a lot of it would not.'
        : 'Very little of this file would survive being challenged.'

  const parts = [
    { label: 'Strong enough to quote', value: s.atOrAboveFloor, tone: 'bg-emerald-500' },
    { label: 'Too weak to quote', value: Math.max(s.belowFloor - s.disputed - s.debunked, 0), tone: 'bg-amber-500' },
    { label: 'Disputed or debunked', value: s.disputed + s.debunked, tone: 'bg-rose-500' },
  ].filter((p) => p.value > 0)
  const total = parts.reduce((n, p) => n + p.value, 0)

  return (
    <div className="space-y-4">
      <div>
        <p className="text-erp-xs uppercase tracking-wide text-muted-foreground">In plain English</p>
        <p className="mt-1 text-erp-lg font-medium">{verdict}</p>
      </div>

      {total > 0 && (
        <div>
          <div className="flex h-5 w-full overflow-hidden rounded-md" role="img"
               aria-label={parts.map((p) => `${p.label}: ${p.value}`).join(', ')}>
            {parts.map((p, i) => (
              <div key={p.label} className="flex h-full" style={{ width: `${(p.value / total) * 100}%` }}>
                {i > 0 && <div className="w-0.5 shrink-0 bg-card" />}
                <div className={`h-full flex-1 ${p.tone}`} title={`${p.label}: ${p.value}`} />
              </div>
            ))}
          </div>
          <ul className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-erp-xs">
            {parts.map((p) => (
              <li key={p.label} className="inline-flex items-center gap-1.5">
                <span aria-hidden className={`h-2.5 w-2.5 rounded-sm ${p.tone}`} />
                {p.label}
                <span className="tabular-nums text-muted-foreground">
                  {p.value.toLocaleString()} · {pct(p.value)}%
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-3">
        <Point label="Can we use it?">
          <b>{solid}%</b> of the {s.items.toLocaleString()} items on file are graded well enough
          to put in front of a journalist or a court — that means both the source is trusted{' '}
          <i>and</i> the specific claim was judged credible, not one or the other.
          {s.belowFloor > 0 && <> The other {s.belowFloor.toLocaleString()} are background only.</>}
        </Point>

        <Point label="Has anyone else said it?">
          {s.corroborated.toLocaleString()} items have a second, independent source.{' '}
          <b>{s.singleSource.toLocaleString()}</b> rest on one source alone — which does not
          make them wrong, only unrepeated. The fix is to go and find the second source.
          {s.singleSourceSubjects > 0 && (
            <> {s.singleSourceSubjects} whole files come from a single publication.</>
          )}
        </Point>

        <Point label="How current is it?">
          {s.latencyP50Minutes > 0
            ? <>Half of what we hold reached us within <b>{hours(s.latencyP50Minutes)}</b> of the
                event; the slowest tenth took over {hours(s.latencyP90Minutes)}.</>
            : <>There is not enough dated material to say how quickly things reach us.</>}
          {s.undated > 0 && (
            <> {s.undated.toLocaleString()} items have no event date at all — those cannot be
              placed on a timeline, and they are not "instant".</>
          )}
        </Point>
      </div>

      {(s.disputed > 0 || s.debunked > 0 || s.withheldSensitive > 0) && (
        <p className="text-erp-xs text-muted-foreground">
          {s.debunked > 0 && `${s.debunked} items have been shown to be false and must never be quoted (they are kept, not deleted, so the correction is on the record). `}
          {s.disputed > 0 && `${s.disputed} are disputed — somebody contests them, which is not the same as their being wrong. `}
          {s.withheldSensitive > 0 && `${s.withheldSensitive} sensitive items are excluded from every figure here because your permissions do not cover them, so read the totals as a floor.`}
        </p>
      )}
    </div>
  )
}

function Point({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-erp-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-0.5 text-erp-sm leading-relaxed">{children}</p>
    </div>
  )
}
